import { useState, useMemo, useEffect } from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'

import { Header } from 'app/KinerjaShared/components/Header'
import { TugasPokok } from 'app/KinerjaGuruPage/components/perencanaan/TugasPokok'
import { useAPIKinerja } from 'api/kinerja/useAPIKinerja'
import { Rangkuman } from 'app/KinerjaGuruPage/components/perencanaan/Rangkuman'
import { PraktikKinerja } from 'app/KinerjaGuruPage/components/perencanaan/PraktikKinerja'
import { PengembanganKompetensi } from 'app/KinerjaGuruPage/components/perencanaan/PengembanganKompetensi'
import { PerilakuKerja } from 'app/KinerjaGuruPage/components/perencanaan/PerilakuKerja'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'

import type { KinerjaStatus } from 'types/kinerja'

const PERENCANAAN_STEPS: Array<PerencanaanStep> = [
  'TUGAS_POKOK',
  'PRAKTIK_KINERJA',
  'PENGEMBANGAN_KOMPETENSI',
  'PERILAKU_KERJA',
  'RANGKUMAN',
]

const PERENCANAAN_STEP_LABELS: Record<PerencanaanStep, string> = {
  TUGAS_POKOK: 'Pelaksanaan Tugas Pokok',
  PRAKTIK_KINERJA: 'Praktik Kinerja',
  PENGEMBANGAN_KOMPETENSI: 'Pengembangan Kompetensi',
  PERILAKU_KERJA: 'Perilaku Kerja',
  RANGKUMAN: 'Rangkuman',
}

type PerencanaanStep =
  | 'TUGAS_POKOK'
  | 'PRAKTIK_KINERJA'
  | 'PENGEMBANGAN_KOMPETENSI'
  | 'PERILAKU_KERJA'
  | 'RANGKUMAN'

const ALLOWED_STATES: Array<KinerjaStatus> = ['NOT_FOUND', 'DRAFT']

export function AjukanPerencanaan() {
  const router = useRouter()

  // -----------------------------------------
  // Handle steps
  // -----------------------------------------
  const [activeStep, setActiveStep] = useState<PerencanaanStep>('TUGAS_POKOK')

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const {
    data: perencanaanData,
    isLoading,
    isSuccess,
  } = useAPIKinerja(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: true,
    },
    {
      tipe: 'SPF',
    }
  )

  const allowedToVisit = useMemo(() => {
    return ALLOWED_STATES.includes(perencanaanData?.data?.status)
  }, [perencanaanData?.data?.status])

  // -----------------------------------------
  // Perencanaan state guard
  // -----------------------------------------
  useEffect(() => {
    if (isSuccess && !allowedToVisit) {
      router.replace(PENGELOLAAN_KINERJA_GURU_PATH)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allowedToVisit, isSuccess])

  return (
    <main className="max-w-7xl mx-auto py-12 px-6">
      <Header title="Perencanaan Kinerja" className="mb-9" />

      {isLoading || !allowedToVisit ? (
        <div className="space-y-6">
          <Shimmer height={128} width="100%" className="rounded" />
          <Shimmer height={320} width="100%" className="rounded" />
          <Shimmer height={440} width="100%" className="rounded" />
        </div>
      ) : (
        <div className="bg-white rounded-lg">
          {/* Header */}
          <div
            className="pt-3 px-9 flex gap-7 border-b border-subdued overflow-x-auto"
            role="tablist"
          >
            {PERENCANAAN_STEPS.map((step, index) => {
              const isActive = step === activeStep
              const isCompleted = index < PERENCANAAN_STEPS.indexOf(activeStep)

              return (
                <div
                  key={step + index}
                  role="tab"
                  id={`step-${step}-tab`}
                  aria-controls={`step-${step}-panel`}
                  aria-selected={isActive}
                  aria-disabled={isCompleted}
                  className={clsx(
                    'flex items-center gap-2 py-3',
                    isActive && 'border-b-4 border-blue-70'
                  )}
                >
                  <div
                    className={clsx(
                      'w-8 h-8 flex-shrink-0 flex-grow-0 rounded-full flex items-center justify-center',
                      isActive
                        ? 'bg-primary text-white'
                        : isCompleted
                        ? 'bg-white text-success-default border border-subdued'
                        : 'bg-white text-disabled border border-disabled'
                    )}
                  >
                    {isCompleted ? (
                      <Icon color="success">check</Icon>
                    ) : (
                      <Text
                        variant="body-bold"
                        color={isActive ? 'inverse' : 'disabled'}
                      >
                        {index + 1}
                      </Text>
                    )}
                  </div>

                  <Text
                    variant="heading-sm"
                    color={
                      isActive
                        ? 'default'
                        : isCompleted
                        ? 'subdued'
                        : 'disabled'
                    }
                    className="whitespace-nowrap"
                    as="h2"
                  >
                    {PERENCANAAN_STEP_LABELS[step]}
                  </Text>
                </div>
              )
            })}
          </div>

          {/* Form Content */}
          {activeStep === 'TUGAS_POKOK' && (
            <div
              role="tabpanel"
              aria-labelledby="step-TUGAS_POKOK-tab"
              id="step-TUGAS_POKOK-panel"
            >
              <TugasPokok onNext={() => setActiveStep('PRAKTIK_KINERJA')} />
            </div>
          )}
          {activeStep === 'PRAKTIK_KINERJA' && (
            <div
              role="tabpanel"
              aria-labelledby="step-PRAKTIK_KINERJA-tab"
              id="step-PRAKTIK_KINERJA-panel"
            >
              <PraktikKinerja
                onNext={() => setActiveStep('PENGEMBANGAN_KOMPETENSI')}
              />
            </div>
          )}
          {activeStep === 'PENGEMBANGAN_KOMPETENSI' && (
            <div
              role="tabpanel"
              aria-labelledby="step-PENGEMBANGAN_KOMPETENSI-tab"
              id="step-PENGEMBANGAN_KOMPETENSI-panel"
            >
              <PengembanganKompetensi
                onNext={() => setActiveStep('PERILAKU_KERJA')}
              />
            </div>
          )}
          {activeStep === 'PERILAKU_KERJA' && (
            <div
              role="tabpanel"
              aria-labelledby="step-PERILAKU_KERJA-tab"
              id="step-PERILAKU_KERJA-panel"
            >
              <PerilakuKerja onNext={() => setActiveStep('RANGKUMAN')} />
            </div>
          )}
          {activeStep === 'RANGKUMAN' && (
            <div
              role="tabpanel"
              aria-labelledby="step-RANGKUMAN-tab"
              id="step-RANGKUMAN-panel"
            >
              <Rangkuman onBack={() => setActiveStep('TUGAS_POKOK')} />
            </div>
          )}
        </div>
      )}
    </main>
  )
}
